import { useRef, useState } from 'react';
import { useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getCalendarEventDetailsEmailContent } from 'shared-helpers/calendar';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { useApiData } from 'lib/api';
import { ModalButtonWithForm } from 'components/common';
import {
  EmailMessageEditor,
  FormGroup,
  RadioGroup
} from 'components/common/form';

import type { FormApi } from 'informed';
import type { GetEmailTemplatesByTypeRes } from 'shared-types/responses';
import type {
  EventChangedValues,
  EventSettingsValues
} from './CalendarEventSettings';
import { EmailTemplateData } from 'shared-types';

interface Props {
  onSubmit: (values: EventChangedValues) => Promise<void>;
}

const CalendarEventSettingsChangedInfo = ({ onSubmit }: Props) => {
  const formState = useFormState<EventSettingsValues>();

  const formApi = useRef<FormApi>(undefined);

  const {
    language,
    messages,
    pageState: { calendarEvent },
    serverConfig: { adminUrl, timeZone },
    user
  } = useReduxState();

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateData[]>([]);
  const [emailTemplatesDefault, setEmailTemplatesDefault] = useState<
    EmailTemplateData | undefined
  >(undefined);
  const [selectedRecipientsType, setSelectedRecipientsType] = useState('none');

  const { data: emailTemplatesData } = useApiData<GetEmailTemplatesByTypeRes>({
    onDataUpdated: (data) => {
      updateEmailTemplates(data);
    },
    url: `/admin/email-templates-data?type=calendar-event-settings-changed&calendarEventId=${calendarEvent.id}`
  });

  const updateEmailTemplates = (
    emailTemplatesData: GetEmailTemplatesByTypeRes
  ) => {
    let templates =
      JSON.parse(JSON.stringify(emailTemplatesData?.emailTemplates)) || [];

    const eventDetails = getCalendarEventDetailsEmailContent({
      calendarEvent: {
        ...formState.values,
        endDate: new Date(formState.values.endDate),
        lastJoinDate: formState.values.lastJoinDate
          ? new Date(formState.values.lastJoinDate)
          : undefined,
        message: calendarEvent.message,
        startDate: new Date(formState.values.startDate)
      },
      messages,
      timeZone
    });

    templates = renderPlaceholdersInTemplates({
      emailTemplates: templates,
      overridePlaceholderValues: {
        contactEmail: user?.email,
        eventDetails,
        eventTitle: calendarEvent.title,
        eventUrl: `${adminUrl}/calendar/${calendarEvent.id}`
      }
    });

    const templatesDefault = getDefaultEmailTemplate({
      language,
      emailTemplates: templates
    });

    setEmailTemplates(templates);
    setEmailTemplatesDefault(templatesDefault);
  };

  const handleRecipientsTypeChange = (value: string) => {
    setSelectedRecipientsType(value);

    if (value === 'all') {
      setTimeout(() => {
        formApi.current.setValue(
          'email.buttonLink',
          emailTemplatesDefault.email.buttonLink
        );
        formApi.current.setValue(
          'email.buttonText',
          emailTemplatesDefault.email.buttonText
        );
        formApi.current.setValue(
          'email.content',
          emailTemplatesDefault.email.content
        );
        formApi.current.setValue(
          'email.contentAfterButton',
          emailTemplatesDefault.email.contentAfterButton
        );
        formApi.current.setValue(
          'email.footnote',
          emailTemplatesDefault.email.footnote
        );
        formApi.current.setValue(
          'email.heading',
          emailTemplatesDefault.email.heading
        );
        formApi.current.setValue(
          'email.subject',
          emailTemplatesDefault.email.subject
        );
      });
    }
  };

  const handleShowModal = () => {
    updateEmailTemplates(emailTemplatesData);
  };

  const handleSubmit = async (values: EventChangedValues) => {
    await onSubmit(values);
  };

  const initialValues = emailTemplatesDefault
    ? {
        email: {
          ...emailTemplatesDefault.email,
          selectedTemplateIdentifier: emailTemplatesDefault.identifier
        },
        recipientsType: 'none'
      }
    : {};

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonText={messages.commonSaveChanges}
      buttonTextHideBreakpoint={breakpoints.small - 1}
      formAllowEmptyStrings={true}
      initialValues={initialValues}
      modalActionText={
        selectedRecipientsType === 'all'
          ? messages.commonSubmit
          : messages.commonSaveChanges
      }
      modalTitle={messages.calendarEventEmailSettingsChanged}
      modalWidth={selectedRecipientsType === 'all' ? 'wide' : undefined}
      onShow={handleShowModal}
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi) => (formApi.current = form)}
    >
      {() => (
        <div>
          <div className="info">
            {messages.calendarEventEmailSettingsChangedSendEmailLabel}
          </div>

          <FormGroup>
            <RadioGroup
              field="recipientsType"
              onChange={handleRecipientsTypeChange}
              options={[
                {
                  id: 'email-recipients-none',
                  label: messages.calendarEventEmailSettingsChangedDoNotSend,
                  value: 'none'
                },
                {
                  id: 'email-recipients-all',
                  label: messages.calendarEventEmailSettingsChangedSendToAll,
                  value: 'all'
                }
              ]}
            />
          </FormGroup>

          {selectedRecipientsType === 'all' && (
            <EmailMessageEditor
              availableTemplates={emailTemplates}
              id="calendar-event-settings-changed"
              style={{ marginTop: 20 }}
            />
          )}
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventSettingsChangedInfo;
